import { useAuth0 } from '@auth0/auth0-react';
import React, { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import RootLayout from './layouts/RootLayout';
import CallbackPage from './pages/CallbackPage/CallbackPage';
import Kindergartens from './pages/Kindergartens/Kindergartens';
import NotFoundPage from './pages/NotFoundPage/NotFoundPage';
import UnauthorizedPage from './pages/UnauthorizedPage/UnathorizedPage';
import { setTokenGetter } from './api/api';
import {
  CALLBACK,
  CHILD_CREATE,
  CHILD_DETAILS,
  CHILD_EDIT,
  GROUP_DETAILS,
  INFOBOARD,
  FINANCE,
  FINANCE_GROUP,
  FINANCE_CONTRACTS,
  FINANCE_DEBTS,
  FINANCE_INVOICES,
  FINANCE_PLATFORM_ITEMS,
  FINANCE_LOCATION,
  FINANCE_SETTINGS,
  INVOICE_BATCH_DETAILS,
  INVOICE_CREATE,
  INVOICE_DETAILS,
  KINDERGARTEN_DETAILS,
  KINDERGARTENS,
  LOCATIONS_CREATE,
  TEACHER_DETAILS,
  TEACHER_EDIT,
  TEACHERS_CREATE,
  UNATHORIZED,
  PARENT_CREATE,
  PARENT_EDIT,
  PARENT_DETAILS,
  POST_CREATE,
  POST_EDIT,
  EVENT_CREATE,
  EVENT_DETAILS
} from './common/constants/routes.constants';
import Infoboard from './pages/Infoboard/Infoboard';
import FinanceInvoicesPage from './pages/Finance/FinanceInvoicesPage';
import FinanceContractsPage from './pages/Finance/FinanceContractsPage';
import FinanceDebtsPage from './pages/Finance/FinanceDebtsPage';
import FinancePlatformItemsPage from './pages/Finance/FinancePlatformItemsPage';
import FinanceDashboard from './pages/Finance/FinanceDashboard';
import FinanceLocationPage from './pages/Finance/FinanceLocationPage';
import FinanceGroupPage from './pages/Finance/FinanceGroupPage';
import FinanceSettingsPage from './pages/Finance/FinanceSettingsPage';
import InvoicePage from './pages/Finance/InvoicePage';
import InvoiceBatchPage from './pages/Finance/InvoiceBatchPage';
import { useUserStore } from './store/useUserStore';
import { getUserAdministrationInfo } from './api/auth.api';
import KindergartenDetails from './pages/KindergartenDetails/KindergartenDetails';
import CreateLocation from './pages/CreateLocation/CreateLocation';
import { ToastContainer } from 'react-toastify';
import GroupDetails from './pages/GroupDetails/GroupDetails';
import TeacherPage from './pages/KindergartenDetails/components/TeacherPage';
import { ActionMode } from './common/enums/common.enum';
import ChildPage from './pages/KindergartenDetails/components/ChildPage';
import ParentPage from './pages/KindergartenDetails/components/ParentPage';
import PostPage from './pages/Infoboard/components/PostPage/PostPage';
import ScreenSizeWarning from './components/ScreenSizeWarning/ScreenSizeWarning';
import CreateConfiguration from './pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration';
import GroupEventConfiguration from './pages/KindergartenDetails/components/GroupEventConfiguration';

function App() {
  const { isLoading, isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const { setUserInfo, userInfo, clear } = useUserStore();

  useEffect(() => {
    setTokenGetter(() => getAccessTokenSilently());
  }, [getAccessTokenSilently]);

  // Refreshed once per session rather than only when the store is empty. userInfo is
  // persisted to localStorage, so a stale copy - from before a role existed, or from
  // before access changed on the server - would otherwise be trusted forever and the
  // user would be stranded on /unauthorized with no way to recover.
  const hasFetchedUserInfo = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !hasFetchedUserInfo.current) {
      hasFetchedUserInfo.current = true;
      fetchUserInfo();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !window.location.pathname.startsWith(CALLBACK)) {
      clear();
      loginWithRedirect();
    }
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  const fetchUserInfo = async () => {
    try {
      const res = await getUserAdministrationInfo();
      setUserInfo(res);
    } catch (err) {
      console.error('Failed to fetch user info');
    }
  };

  if (isLoading || (!isAuthenticated && !window.location.pathname.startsWith(CALLBACK))) {
    return null;
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
      <ScreenSizeWarning />
      <Routes>
        <Route path="/" element={<RootLayout />}>
          <Route path={CALLBACK} element={<CallbackPage />} />
          <Route
            path={KINDERGARTENS}
            element={
              <ProtectedRoute>
                <Kindergartens />
              </ProtectedRoute>
            }
          />
          <Route
            path={KINDERGARTEN_DETAILS}
            element={
              <ProtectedRoute>
                <KindergartenDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={LOCATIONS_CREATE}
            element={
              <ProtectedRoute>
                <CreateLocation />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHERS_CREATE}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHER_EDIT}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={TEACHER_DETAILS}
            element={
              <ProtectedRoute>
                <TeacherPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_CREATE}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_EDIT}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={CHILD_DETAILS}
            element={
              <ProtectedRoute>
                <ChildPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_CREATE}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_EDIT}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route
            path={PARENT_DETAILS}
            element={
              <ProtectedRoute>
                <ParentPage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={GROUP_DETAILS}
            element={
              <ProtectedRoute>
                <GroupDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path={INFOBOARD}
            element={
              <ProtectedRoute>
                <Infoboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE}
            element={
              <ProtectedRoute>
                <FinanceDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_INVOICES}
            element={
              <ProtectedRoute>
                <FinanceInvoicesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_CONTRACTS}
            element={
              <ProtectedRoute>
                <FinanceContractsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_DEBTS}
            element={
              <ProtectedRoute>
                <FinanceDebtsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_PLATFORM_ITEMS}
            element={
              <ProtectedRoute>
                <FinancePlatformItemsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_SETTINGS}
            element={
              <ProtectedRoute>
                <FinanceSettingsPage />
              </ProtectedRoute>
            }
          />
          {/* declared before FINANCE_LOCATION so the group page wins the longer match */}
          <Route
            path={FINANCE_GROUP}
            element={
              <ProtectedRoute>
                <FinanceGroupPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={FINANCE_LOCATION}
            element={
              <ProtectedRoute>
                <FinanceLocationPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={INVOICE_BATCH_DETAILS}
            element={
              <ProtectedRoute>
                <InvoiceBatchPage />
              </ProtectedRoute>
            }
          />
          {/* declared before INVOICE_DETAILS so 'create' is never read as an id */}
          <Route
            path={INVOICE_CREATE}
            element={
              <ProtectedRoute>
                <InvoicePage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={INVOICE_DETAILS}
            element={
              <ProtectedRoute>
                <InvoicePage mode={ActionMode.DETAILS} />
              </ProtectedRoute>
            }
          />
          <Route
            path={POST_CREATE}
            element={
              <ProtectedRoute>
                <PostPage mode={ActionMode.CREATE} />
              </ProtectedRoute>
            }
          />
          <Route
            path={POST_EDIT}
            element={
              <ProtectedRoute>
                <PostPage mode={ActionMode.EDIT} />
              </ProtectedRoute>
            }
          />
          <Route path={UNATHORIZED} element={<UnauthorizedPage />} />
          <Route
            path="*"
            element={
              <ProtectedRoute>
                <NotFoundPage />
              </ProtectedRoute>
            }
          />
          <Route
            path={EVENT_CREATE}
            element={
              <ProtectedRoute>
                <CreateConfiguration />
              </ProtectedRoute>
            }
          />
          <Route
            path={EVENT_DETAILS}
            element={
              <ProtectedRoute>
                <GroupEventConfiguration />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </>
  );
}

export default App;
